import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Loader2 } from "lucide-react";
import ModalCloseButton from "./ModalCloseButton";
import translations from "./Translations";

/**
 * Centred preview of one attachment in a white-bordered card. `kind` is
 * "image" (fitted to the viewport) or "pdf" (shown in an iframe). Rendered in
 * a portal so it centres on the screen even when opened from a slider panel.
 * Closes on Esc, on the close button, or on a click outside the content.
 */
const AttachmentPreviewModal = ({ url, fileName, kind, onClose, onDownload, downloading = false, langMode }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [url]);

  useEffect(() => {
    if (!url) return undefined;
    // Capture phase + stopPropagation: Esc closes only this preview, not the
    // attachments dialog it may have been opened from.
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [url, onClose]);

  if (!url) return null;

  const t = translations[langMode] || {};

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={fileName}
      onClick={onClose}
    >
      {/* Small content sits centred in the minimum box; large content is
          scaled down to fit the maximum (85% of the screen), fully visible. */}
      <div
        className="relative flex min-h-[240px] min-w-[min(320px,85vw)] max-h-[85vh] max-w-[85vw] items-center justify-center overflow-hidden rounded-xl border-4 border-white bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
          {onDownload && !failed && (
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              title={t.download ?? "Download"}
              aria-label={t.download ?? "Download"}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200/80 bg-white text-slate-500 shadow transition-colors hover:bg-blue-50 hover:text-[#236DB4] disabled:cursor-wait disabled:opacity-60"
            >
              {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            </button>
          )}
          <ModalCloseButton onClick={onClose} className="bg-white shadow" ariaLabel="Close preview" />
        </div>

        {failed ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-slate-600">
            <FileText size={32} className="text-slate-400" />
            {t.attachmentUnavailable ?? "Attachment is not available"}
          </div>
        ) : kind === "pdf" ? (
          // A PDF has no natural size, so it takes the maximum box.
          <iframe
            src={url}
            title={fileName}
            className="block bg-white"
            style={{ height: "calc(85vh - 8px)", width: "calc(85vw - 8px)", maxWidth: "56rem" }}
          />
        ) : (
          <>
            {!loaded && <Loader2 size={28} className="animate-spin text-[#236DB4]" />}
            <img
              src={url}
              alt={fileName}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              className={`block h-auto w-auto object-contain ${loaded ? "" : "hidden"}`}
              style={{ maxHeight: "calc(85vh - 8px)", maxWidth: "calc(85vw - 8px)" }}
            />
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AttachmentPreviewModal;
